import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { commitMutation } from "react-relay";

import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { Node as ProseNode } from "prosemirror-model";
import { graphql } from "relay-runtime";

import ActionItemActions from "~/actionItems/ActionItemActions";
import ActionItemCheckbox from "~/actionItems/ActionItemCheckbox";
import ActionItemMeta from "~/actionItems/ActionItemMeta";
import ActionItemText from "~/actionItems/ActionItemText";
import ActionItemWidgets from "~/actionItems/ActionItemWidgets";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem as ActionItemType } from "~/actionItems/types";
import { ChangeAction } from "~/lib/prose/BlockTextEditor";
import { markText } from "~/lib/prose/utils";
import { isCtrlOrCmd, isSafari } from "~/lib/utils";
import { environment } from "~/models";

import { Views } from "./constants";

import type { ActionItemChangeContentMutation } from "~/__generated__/ActionItemChangeContentMutation.graphql";

import styles from "./ActionItem.module.css";

// TODO: For an improvement we should fetch all notes, streams and assignees
// At the main component and only have the noteId, streamId and assigneeId here
// We would them use those lists to get the note's/stream's/assigne's content
graphql`
	fragment ActionItemData on NoteBlock {
		id
		key
		type
		text
		done
		wontDo
		aiGenerated
		recordingId
		recordingOffset
		hasOriginalBlock
		updatedAt
		dueDate
		createdAt
		userOrder
		userOrderWithinGroup
		entityRanges
		assigneeChangedAt
		creatorId
		assigneeId
		noteId
		hasAccessToNote
		hasLink
		statusCondition
		completedBy {
			id
			fullName
		}
		originalBlock {
			id
			noteId
		}
		note {
			id
			isLocked
			entityMap
			stream {
				__typename
				id
				displayName
				isTodoStream
				isExternal
				url
				... on NoteStream {
					type
				}
			}
			noteuserSet {
				id
				user {
					id
					email
					firstName
					fullName
					image
					isExternal

					internalAttributes {
						invitable
					}
				}
			}
			... on Note {
				type
				url
			}
		}
		assignments {
			status
			assignee {
				id
				fullName
				image
				isGuest
				isExternal
				email
				isActive
			}
		}
		creator {
			id
			fullName
			image
			isGuest
			isExternal
		}
		...actionItemIntegrationLinkNoteBlock @relay(mask: false)
		...asanaItemsNoteBlock @relay(mask: false)
	}
`;

const changeActionItemContentMutation = graphql`
	mutation ActionItemChangeContentMutation($input: SetBlockContentInput!) {
		setBlockContent(input: $input) {
			note {
				id
				entityMap
			}
			block {
				id
				entityRanges
				text
				updatedAt
			}
		}
	}
`;

export const createActionItemsMutation = graphql`
	mutation ActionItemCreateMutation($input: CreateTodoInput!) {
		createTodo(input: $input) {
			block {
				...ActionItemData @relay(mask: false)
			}
		}
	}
`;

export const deleteActionItemsMutation = graphql`
	mutation ActionItemDeleteMutation($input: DeleteBlocksInput!) {
		deleteBlocks(input: $input) {
			deletedIds
		}
	}
`;

export const changeActionItemsStatusMutation = graphql`
	mutation ActionItemChangeStatusMutation($input: ToggleBlocksFieldInput!) {
		toggleBlocksField(input: $input) {
			noteBlockEdges {
				node {
					id
					done
					wontDo
					updatedAt
					completedBy {
						id
						fullName
					}
					assignments {
						status
						assignee {
							id
							fullName
							image
							isGuest
							isExternal
							email
						}
					}
				}
			}
		}
	}
`;

export const changeActionItemsAttrsMutation = graphql`
	mutation ActionItemChangeAttrsMutation($input: UpdateBlocksAttrsInput!) {
		updateBlocksAttrs(input: $input) {
			blocks {
				id
				dueDate
				dueDatePriority
				entityRanges
				assigneeId
				statusCondition
				completedBy {
					id
					fullName
				}
				assignments {
					status
					assignee {
						id
						fullName
						email
						title
						department
						isActive
						isConfigured
						isGuest
						isExternal
						image
						internalAttributes {
							manager {
								id
								fullName
							}
						}
					}
				}
				done
			}
		}
	}
`;

type ActionItemProps = {
	item: ActionItemType;
	adjacentItemSelect(item: ActionItemType): void;
	inAISuggestions?: boolean;
} & Omit<React.HTMLProps<HTMLDivElement>, "ref">;

const ActionItem = forwardRef<HTMLDivElement, ActionItemProps>(function ActionItem(
	{ item, adjacentItemSelect, inAISuggestions, ...rest },
	ref,
) {
	const { id, hasAccessToNote, note, done, wontDo, aiGenerated, hasOriginalBlock, assignments, statusCondition } = item;
	const [editing, setEditing] = useState<boolean>(false);
	const {
		selectedActionItems,
		setSelectedActionItems,
		filters,
		itemBeingEdited,
		setItemBeingEdited,
		onDeleteItem,
		draggingId,
		inPanel,
		renderingContext,
		view,
	} = useContext(ActionItemsContext);
	const { search } = filters;

	const inSeriesPanel = renderingContext === "seriesPanel";

	// We don't want to have 2 items being edited at the same time
	// There are some flows that start a different item to be edited
	// Without this one being aware, so we handle it here
	useEffect(() => {
		if (!itemBeingEdited) {
			return;
		}
		if (itemBeingEdited.id !== id && editing) {
			setEditing(false);
		}
	}, [id, editing, itemBeingEdited]);

	const selected = useMemo(
		() => selectedActionItems.some(selectedActionItem => selectedActionItem.id === id),
		[selectedActionItems, id],
	);

	const editingDisabled = useMemo(
		() => (!hasAccessToNote && !!note?.url) || note?.isLocked || hasOriginalBlock,
		[hasAccessToNote, note, hasOriginalBlock],
	);

	const checkboxDisabled = useMemo(() => !hasAccessToNote && !!note?.url, [hasAccessToNote, note]);

	// We want to apply styles the item when we are dragging multiple items and this is not the one that was clicked.
	const inMultiDragging = useMemo(
		() => Boolean(draggingId && selected && draggingId !== id),
		[draggingId, selected, id],
	);
	// We want to apply styles the item when we are dragging a single item. Items can be dragged without being selected,
	// so a selectedActionItems.length check allows 0 as well.
	const inSingleDragging = useMemo(
		() => Boolean(draggingId && draggingId === id && selectedActionItems.length <= 1),
		[draggingId, id, selectedActionItems],
	);

	// We want to apply styles to the head of the multi dragging.
	const headOfMultiDragging = useMemo(
		() => Boolean(draggingId && draggingId === id && selectedActionItems.length > 1),
		[draggingId, id, selectedActionItems],
	);

	const highlighted = useMemo(() => search.split(" ") || [], [search]);
	const transforms = React.useMemo(() => {
		return highlighted.map(str => {
			return (doc: ProseNode) => {
				try {
					return markText(doc, str, doc.type.schema.marks["highlight"].create());
				} catch (error) {
					console.warn(error);
					return doc;
				}
			};
		});
	}, [highlighted]);

	const onSelect = useCallback(
		(additionalSelect?: boolean, adjacentSelect?: boolean) => {
			if (!setSelectedActionItems) {
				return;
			}
			if (selected) {
				if (additionalSelect) {
					setSelectedActionItems(selectedActionItems => selectedActionItems.filter(item => item.id !== id));
				}
				return;
			}
			if (adjacentSelect) {
				adjacentItemSelect(item);
				return;
			}
			setSelectedActionItems(selectedActionItems => (additionalSelect ? [...selectedActionItems, item] : [item]));
			setEditing(false);
		},
		[item, id, selected, setSelectedActionItems, adjacentItemSelect],
	);

	const onClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (editing) {
				return;
			}
			onSelect(isCtrlOrCmd(e.nativeEvent), e.shiftKey);
		},
		[onSelect, editing],
	);

	const onStartEditing = useCallback(
		(force?: boolean) => {
			if (!force && (!selected || editingDisabled)) {
				return;
			}
			setEditing(true);
			setItemBeingEdited?.(item);
		},
		[item, selected, setItemBeingEdited, editingDisabled],
	);

	const onClickContent = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const multiSelect = isCtrlOrCmd(e.nativeEvent) || e.shiftKey;
			if (multiSelect) {
				return;
			}
			onStartEditing();
		},
		[onStartEditing],
	);

	const onDelete = useCallback(() => {
		onDeleteItem(item);
	}, [item, onDeleteItem]);

	const stopEditing = useCallback(() => {
		setEditing(false);
		if (itemBeingEdited?.id === id) {
			setItemBeingEdited?.(undefined);
		}
	}, [itemBeingEdited, setItemBeingEdited, id]);

	const onContentChanged = useCallback(
		(prevDoc: ProseNode, doc: ProseNode, action: ChangeAction = ChangeAction.blur) => {
			if (action !== ChangeAction.debounce) {
				stopEditing();
			}
			if (!note?.stream || prevDoc.eq(doc) || action === ChangeAction.esc) return;
			if (prevDoc.textContent.length && !doc.textContent.length) {
				if (action === ChangeAction.debounce) return;
				onDelete();
				return;
			}
			commitMutation<ActionItemChangeContentMutation>(environment, {
				mutation: changeActionItemContentMutation,
				variables: {
					input: {
						id: id,
						content: doc.toJSON().content,
					},
				},
				optimisticUpdater: store => {
					const record = store.get(id);
					if (!record) return;
					record?.setValue(doc.textBetween(0, doc.nodeSize - 2, undefined, "\n"), "text");
				},
			});
		},
		[id, note, onDelete, stopEditing],
	);

	const assignees = useMemo(() => {
		if (!assignments || statusCondition !== StatusCondition.ALL) return [];
		return (
			assignments.map(assignment => {
				return {
					id: assignment.assignee.id,
					status: assignment.status || NoteBlockStatus.NOT_DONE,
					attendeeAttrs: assignment.assignee,
				};
			}) || []
		);
	}, [statusCondition, assignments]);

	const metaContent = inSeriesPanel ? (
		<></>
	) : (
		<ActionItemMeta
			item={item}
			assignees={assignees}
			highlightedText={highlighted}
			className={styles.meta}
			inAISuggestions={inAISuggestions}
		/>
	);

	const animatedMeta = (
		<AnimatePresence>
			{selected && (
				<motion.div
					initial={{ height: 0 }}
					animate={{ height: "1rem" }}
					exit={{ height: 0 }}
					key={item.id}
					transition={{ ease: "easeIn", duration: 0.2 }}
				>
					{metaContent}
				</motion.div>
			)}
		</AnimatePresence>
	);

	const inRecording = view === Views.RECORDING;

	return (
		<div
			ref={ref}
			{...rest}
			onClick={onClick}
			className={cx(styles.actionItem, {
				[styles.aiGenerated]: aiGenerated,
				[styles.selected]: selected,
				[styles.editing]: editing,
				[styles.headOfMultiDragging]: headOfMultiDragging,
				[styles.inSingleDragging]: inSingleDragging,
				[styles.inMultiDragging]: inMultiDragging,
				[styles.actionItemInPanel]: inPanel,
				[styles.actionItemInRecording]: inRecording,
				[styles.actionItemInSeriesPanel]: inSeriesPanel,
				[styles.twoOrMoreChildren]: selectedActionItems.length > 2,
				[styles.aiActionItem]: inAISuggestions,
			})}
			draggable={false}
			data-block-id={id}
			data-is-action-item
		>
			<ActionItemCheckbox
				id={id}
				done={done}
				wontDo={wontDo}
				hasOriginalBlock={hasOriginalBlock}
				inMultiDragging={inMultiDragging}
				isDisabled={checkboxDisabled}
				assignees={assignees}
				statusCondition={statusCondition as StatusCondition}
				aiGenerated={aiGenerated}
			/>
			<div className={styles.actionItemInner}>
				<div
					className={cx(styles.actionItemContent, {
						[styles.textTruncated]: !selected && !editing && inPanel,
						[styles.safari]: isSafari,
					})}
					onClick={onClickContent}
				>
					<div
						className={cx(styles.actionItemTextAndWidgets, {
							[styles.inRecording]: inRecording,
						})}
					>
						<ActionItemText
							item={item}
							transforms={transforms}
							editing={editing}
							onContentChange={onContentChanged}
							inMultiDragging={inMultiDragging}
							className={cx({
								[styles.inNavPanel]: inPanel,
								[styles.inSeriesPanel]: inSeriesPanel,
								[styles.inRecording]: inRecording,
							})}
							readOnly={editingDisabled}
						/>
						<ActionItemWidgets item={item} />
					</div>
					<ActionItemActions
						item={item}
						onSelect={onSelect}
						onStartEditing={onStartEditing}
						className={styles.actions}
						withAssignee
						editingDisabled={editingDisabled}
						showDueDate={!editing}
						selected={selected}
					/>
				</div>
				{!inRecording && (inPanel && !editing ? animatedMeta : metaContent)}
			</div>
			{headOfMultiDragging && <span className={styles.multiDraggingCount}>+{selectedActionItems.length - 1}</span>}
		</div>
	);
});

export default ActionItem;
